import { transformCitationsFromApi } from "@/services/api/transformCitationsFromApi"
import { ApiCitation } from "@/types/questionnaire/api"
import { ToolStep, TurnBlock } from "../types"

/**
 * Accumulates one CHAT TURN into ordered text and tool-group blocks.
 *
 * A turn is one user message and everything that follows it — including the
 * continuation round-trips a client-executed tool causes, which are separate
 * HTTP requests but one turn from the user's point of view. So the
 * accumulator is created per user message and handed to each round, rather
 * than living inside a single stream handler.
 *
 * Grouping is STRUCTURAL, never derived from reading the text: a tool call
 * closes the open text block, and a text chunk closes the open tool group.
 */
export interface TurnAccumulator {
  blocks: TurnBlock[]
  /** Flat concatenation of every text chunk; becomes `Message.content`. */
  flatText: string
}

export function createTurnAccumulator(): TurnAccumulator {
  return { blocks: [], flatText: "" }
}

/** Blocks are replaced, never mutated: React already rendered from the array
 * snapshots handed out by earlier updates, and mutating a block in place would
 * retroactively change what those snapshots describe. */
function replaceLast(accumulator: TurnAccumulator, block: TurnBlock): void {
  accumulator.blocks = [...accumulator.blocks.slice(0, -1), block]
}

function lastBlock(accumulator: TurnAccumulator): TurnBlock | undefined {
  return accumulator.blocks[accumulator.blocks.length - 1]
}

export function appendText(
  accumulator: TurnAccumulator,
  text: string,
  now: number = Date.now(),
): TurnAccumulator {
  accumulator.flatText += text
  const last = lastBlock(accumulator)
  if (last?.kind === "text") {
    replaceLast(accumulator, { kind: "text", text: last.text + text })
    return accumulator
  }
  // Narration is what ends a tool group. NOT the last tool settling: the agent
  // loop runs tools one after another (start, finish, start, finish), so
  // closing on "every step settled" would put each tool in its own group and
  // there would be nothing to group.
  if (last?.kind === "tools" && last.endedAt === undefined) {
    replaceLast(accumulator, { ...last, endedAt: now })
  }
  accumulator.blocks = [...accumulator.blocks, { kind: "text", text }]
  return accumulator
}

/** Open a tool group, or join the one already at the end. */
export function startTool(
  accumulator: TurnAccumulator,
  tool: { toolCallId: string; tool: string; input?: Record<string, unknown> },
  now: number = Date.now(),
): TurnAccumulator {
  const step: ToolStep = {
    toolCallId: tool.toolCallId,
    tool: tool.tool,
    status: "running",
    input: tool.input,
  }
  const last = lastBlock(accumulator)
  if (last?.kind === "tools" && last.endedAt === undefined) {
    replaceLast(accumulator, { ...last, steps: [...last.steps, step] })
    return accumulator
  }
  accumulator.blocks = [
    ...accumulator.blocks,
    { kind: "tools", steps: [step], startedAt: now },
  ]
  return accumulator
}

/** A tool that REFUSES returns `{error: ...}` as a normal result, so the
 * backend reports the phase as `completed` — `failed` is reserved for a
 * handler that raised. Both are failures to a reader, and without this a
 * refusal ("No inquiry ... in this organization.") rendered under a green
 * tick. */
function resultCarriesError(result?: Record<string, unknown>): boolean {
  return typeof result?.error === "string" && result.error.trim().length > 0
}

/** Replace one step by id, anywhere in the turn, with fresh block and step
 * objects: a block already handed to `setStreamingMessage` must never change
 * under it. An id with no matching step is ignored. */
function updateStep(
  accumulator: TurnAccumulator,
  toolCallId: string,
  update: (step: ToolStep) => ToolStep,
): TurnAccumulator {
  for (let index = 0; index < accumulator.blocks.length; index += 1) {
    const block = accumulator.blocks[index]
    if (block.kind !== "tools") continue
    const stepIndex = block.steps.findIndex(
      (step) => step.toolCallId === toolCallId,
    )
    if (stepIndex === -1) continue
    const steps = [...block.steps]
    steps[stepIndex] = update(steps[stepIndex])
    accumulator.blocks = [
      ...accumulator.blocks.slice(0, index),
      { ...block, steps },
      ...accumulator.blocks.slice(index + 1),
    ]
    return accumulator
  }
  return accumulator
}

/**
 * Patch a running step by id, anywhere in the turn. A completion with no
 * matching start is ignored rather than inventing a step: it means we joined
 * the stream late, and a step with no label is worse than no step.
 */
export function finishTool(
  accumulator: TurnAccumulator,
  toolCallId: string,
  outcome: { failed: boolean; result?: Record<string, unknown> },
): TurnAccumulator {
  // Deliberately does NOT close the group — see appendText.
  return updateStep(accumulator, toolCallId, (step) => ({
    ...step,
    status:
      outcome.failed || resultCarriesError(outcome.result) ? "error" : "done",
    result: outcome.result,
  }))
}

/** Keep the citations a tool call found on its step, so its row can list
 * them. Ignored for a call it never saw start, as in `finishTool`. */
export function attachToolCitations(
  accumulator: TurnAccumulator,
  toolCallId: string,
  citations: ApiCitation[],
): TurnAccumulator {
  return updateStep(accumulator, toolCallId, (step) => ({
    ...step,
    citations,
  }))
}

/**
 * The turn is over. Closes a tool group still marked open — which happens when
 * a tool never reported a completion (an aborted stream, a dropped frame), and
 * would otherwise leave the group shimmering forever.
 */
export function finalizeTurn(
  accumulator: TurnAccumulator,
  now: number = Date.now(),
): TurnAccumulator {
  accumulator.blocks = accumulator.blocks.map((block) =>
    block.kind === "tools" && block.endedAt === undefined
      ? {
          ...block,
          endedAt: now,
          steps: block.steps.map((step) =>
            step.status === "running" ? { ...step, status: "error" } : step,
          ),
        }
      : block,
  )
  return accumulator
}

/**
 * What the renderer gets. `undefined` for a turn that ran no tools — that
 * renders identically from `content`, so carrying a one-element array would be
 * redundant state.
 */
export function turnBlocks(
  accumulator: TurnAccumulator,
): TurnBlock[] | undefined {
  return accumulator.blocks.some((block) => block.kind === "tools")
    ? accumulator.blocks
    : undefined
}

/** Live view during streaming. Same list: text blocks already hold whatever
 * has arrived, so there is nothing pending to splice in. */
export function streamingBlocks(accumulator: TurnAccumulator): TurnBlock[] {
  return accumulator.blocks
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

function hydrateDetailParams(raw: unknown): ToolStep["detailParams"] {
  if (!isRecord(raw)) return undefined
  const params: NonNullable<ToolStep["detailParams"]> = {}
  if (typeof raw.shown === "number") params.shown = raw.shown
  if (typeof raw.total === "number") params.total = raw.total
  if (typeof raw.count === "number") params.count = raw.count
  if (typeof raw.resource === "string") params.resource = raw.resource
  return Object.keys(params).length > 0 ? params : undefined
}

function hydrateStep(raw: unknown): ToolStep | undefined {
  if (!isRecord(raw)) return undefined
  if (typeof raw.tool !== "string" || typeof raw.toolCallId !== "string") {
    return undefined
  }
  return {
    toolCallId: raw.toolCallId,
    tool: raw.tool,
    // A persisted step is never still running: it was written after the turn
    // finished, so a stored "running" would be a spinner that never resolves.
    status: raw.status === "error" ? "error" : "done",
    detail: typeof raw.detail === "string" ? raw.detail : undefined,
    // Present only for the two structurally-derivable shapes — see
    // toolPresenters.ts. Absent (falls back to `detail` prose) for a row
    // persisted before this key existed, or for error/summary text.
    detailKey: typeof raw.detailKey === "string" ? raw.detailKey : undefined,
    detailParams: hydrateDetailParams(raw.detailParams),
    chips: Array.isArray(raw.chips)
      ? raw.chips.filter((chip): chip is string => typeof chip === "string")
      : undefined,
    // Stored as the API's own citation JSON, the shape `citations_json` has.
    citations: Array.isArray(raw.citations)
      ? transformCitationsFromApi(raw.citations.filter(isRecord))
      : undefined,
  }
}

/**
 * Rebuild a turn's blocks from `message_metadata.activity_json`, so a reopened
 * conversation renders through the same path as a live one.
 *
 * Validated field by field rather than cast: the column is a JSON blob that
 * older rows, a partial write or a future writer could shape differently, and
 * a bad row must degrade to "no groups, render `content`" rather than throw
 * inside the message list.
 */
export function hydrateTurnBlocks(
  activityJson: unknown,
): TurnBlock[] | undefined {
  if (!isRecord(activityJson) || !Array.isArray(activityJson.blocks)) {
    return undefined
  }
  const blocks: TurnBlock[] = []
  for (const raw of activityJson.blocks) {
    if (!isRecord(raw)) continue
    if (raw.kind === "text") {
      if (typeof raw.text === "string" && raw.text) {
        blocks.push({ kind: "text", text: raw.text })
      }
      continue
    }
    if (raw.kind !== "tools" || !Array.isArray(raw.steps)) continue
    const steps = raw.steps
      .map(hydrateStep)
      .filter((step): step is ToolStep => step !== undefined)
    if (steps.length === 0) continue
    const startedAt = typeof raw.startedAt === "number" ? raw.startedAt : 0
    blocks.push({
      kind: "tools",
      steps,
      startedAt,
      // Always closed: a rehydrated turn is over, and an open group would
      // leave the row shimmering forever.
      endedAt: typeof raw.endedAt === "number" ? raw.endedAt : startedAt,
    })
  }
  return blocks.some((block) => block.kind === "tools") ? blocks : undefined
}
